import { calculateMonthlyPayment } from '../utils/loanAmortization';
import { HOME_LOAN_PLANNER_ID, isBlankLoanPlannerSnapshot } from '../utils/loanPlannerSeed';
import { getAnalyticsSummary } from './analyticsService';
import { getHousehold } from './householdService';
import { fetchLiabilities } from './liabilityService';
import { fetchLoanPlannerRemote } from './loanPlannerService';
import { fetchPortfolioSnapshots } from './portfolioSyncService';
import { listTransactions } from './transactionService';

export type ClientMonthSlice = {
  periodKey: string;
  spendTotal: number;
  txCount: number;
  categories: Array<{ category: string; total: number; count: number }>;
  completeness: 'complete' | 'partial' | 'sparse';
  coverageNote: string;
};

/**
 * Shift a `YYYY-MM` key by `delta` months (UTC).
 */
export function shiftMonthKey(periodKey: string, delta: number): string {
  const [y, m] = periodKey.split('-').map(Number);
  const d = new Date(Date.UTC(y!, (m! - 1) + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function currentMonthKey(now = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function daysInMonth(periodKey: string): number {
  const [y, m] = periodKey.split('-').map(Number);
  return new Date(Date.UTC(y!, m!, 0)).getUTCDate();
}

function classifyFromDailySpan(params: {
  periodKey: string;
  txCount: number;
  firstDate?: string;
  lastDate?: string;
}): { completeness: 'complete' | 'partial' | 'sparse'; coverageNote: string } {
  const { periodKey, txCount, firstDate, lastDate } = params;
  const dim = daysInMonth(periodKey);
  const isCurrent = periodKey === currentMonthKey();
  if (txCount < 3 || !firstDate || !lastDate) {
    return {
      completeness: 'sparse',
      coverageNote: txCount === 0 ? 'No transactions imported' : 'Very few transactions',
    };
  }
  const firstDay = Number(firstDate.slice(8, 10));
  const lastDay = Number(lastDate.slice(8, 10));
  const span = lastDay - firstDay;
  const nearFull = span >= Math.min(20, dim - 4) || (firstDay <= 7 && lastDay >= Math.min(24, dim));
  if (isCurrent) {
    const today = new Date().getUTCDate();
    if (today >= dim - 2 && lastDay >= today - 1 && nearFull) {
      return {
        completeness: 'complete',
        coverageNote: `Days ${firstDay}–${lastDay} of ${periodKey} (month ending)`,
      };
    }
    return {
      completeness: nearFull ? 'partial' : 'sparse',
      coverageNote: `Only days ${firstDay}–${lastDay} of ${periodKey} (in-progress / incomplete)`,
    };
  }
  if (nearFull || lastDay >= Math.min(25, dim)) {
    return {
      completeness: 'complete',
      coverageNote: `Days ${firstDay}–${lastDay} of ${periodKey}`,
    };
  }
  return {
    completeness: 'partial',
    coverageNote: `Only days ${firstDay}–${lastDay} of ${periodKey} — not a full month`,
  };
}

/**
 * Build a rich finance snapshot on the client from existing budget-server APIs.
 * Used when GET /assistant/context is unavailable, and as a guaranteed push into ai-assistant.
 */
export async function buildClientFinanceContext(params: {
  accessToken: string;
  userId: string;
  view?: string;
  monthlyIncome?: number;
  /** Viewer plan for assistant entitlement awareness. */
  plan?: 'free' | 'pro';
  name?: string;
  birthYear?: number;
  retirementAge?: number;
  assumedReturnPct?: number;
}): Promise<Record<string, unknown>> {
  const view = params.view === 'combined' ? 'combined' : 'personal';
  const latestKey = currentMonthKey();
  const monthKeys = [0, -1, -2, -3].map((d) => shiftMonthKey(latestKey, d));

  const monthResults = await Promise.all(
    monthKeys.map(async (periodKey) => {
      try {
        const summary = await getAnalyticsSummary({
          accessToken: params.accessToken,
          userId: params.userId,
          period: 'month',
          periodKey,
          view,
        });
        // Sample txs to learn date coverage for this month.
        let firstDate: string | undefined;
        let lastDate: string | undefined;
        try {
          const txRes = await listTransactions({
            accessToken: params.accessToken,
            tab: 'spend',
            period: 'month',
            periodKey,
            view,
            limit: 100,
          });
          const dates = txRes.transactions
            .map((t) => String(t.date).slice(0, 10))
            .filter(Boolean)
            .sort();
          firstDate = dates[0];
          lastDate = dates[dates.length - 1];
        } catch {
          // coverage optional
        }
        const txCount = summary.spend.categories.reduce((s, c) => s + c.transactionCount, 0);
        const { completeness, coverageNote } = classifyFromDailySpan({
          periodKey,
          txCount,
          firstDate,
          lastDate,
        });
        const slice: ClientMonthSlice = {
          periodKey,
          spendTotal: summary.spend.totalSpend,
          txCount,
          categories: summary.spend.categories.slice(0, 8).map((c) => ({
            category: c.categoryName,
            total: c.totalAmount,
            count: c.transactionCount,
          })),
          completeness,
          coverageNote,
        };
        return slice;
      } catch {
        return {
          periodKey,
          spendTotal: 0,
          txCount: 0,
          categories: [],
          completeness: 'sparse' as const,
          coverageNote: 'Could not load this month',
        };
      }
    }),
  );

  const withActivity = monthResults.filter((m) => m.txCount > 0);
  const completePast = withActivity
    .filter((m) => m.completeness === 'complete' && m.periodKey !== latestKey)
    .sort((a, b) => b.periodKey.localeCompare(a.periodKey));
  const analysis =
    completePast[0] ??
    withActivity
      .filter((m) => m.completeness === 'complete')
      .sort((a, b) => b.periodKey.localeCompare(a.periodKey))[0] ??
    withActivity
      .filter((m) => m.periodKey !== latestKey)
      .sort((a, b) => b.txCount - a.txCount)[0] ??
    withActivity[0] ??
    monthResults[1] ??
    monthResults[0]!;

  const skipped = withActivity.filter(
    (m) =>
      m.periodKey !== analysis.periodKey &&
      m.completeness !== 'complete' &&
      m.periodKey >= analysis.periodKey,
  );

  let portfolio: Record<string, unknown> = { total: 0, accounts: [], snapshots: [] };
  try {
    const snaps = await fetchPortfolioSnapshots(6, view);
    const latest = snaps.latest;
    portfolio = {
      total: latest?.totalBalance ?? 0,
      asOfDate: latest?.asOfDate ?? null,
      broker: latest?.broker ?? null,
      accounts: (latest?.accounts ?? []).slice(0, 20).map((a) => ({
        label: a.label,
        balance: a.balance,
        type: a.type,
        isInvestment: a.isInvestment !== false,
        expectedReturnPct: a.expectedReturnPct,
      })),
      recentSnapshots: (snaps.snapshots ?? []).slice(0, 4).map((s) => ({
        asOfDate: s.asOfDate,
        totalBalance: s.totalBalance,
        accountCount: s.accounts?.length ?? 0,
      })),
    };
  } catch {
    // portfolio optional
  }

  let liabilities: Array<Record<string, unknown>> = [];
  let liabilitiesTotal = 0;
  let liabilitiesMonthlyPaymentTotal = 0;
  try {
    const rows = await fetchLiabilities(view);
    liabilities = rows.slice(0, 20).map((l) => ({
      liabilityId: l.liabilityId,
      type: l.type,
      label: l.label,
      balance: l.balance,
      interestRatePct: l.interestRatePct,
      monthlyPayment: l.monthlyPayment,
      termYears: l.termYears,
      ownerUserId: l.ownerUserId,
    }));
    liabilitiesTotal = rows.reduce((s, l) => s + (l.balance || 0), 0);
    liabilitiesMonthlyPaymentTotal = rows.reduce(
      (s, l) => s + (l.monthlyPayment || 0),
      0,
    );
  } catch {
    // liabilities optional
  }

  let partners: Array<Record<string, unknown>> | undefined;
  let household: Record<string, unknown> | undefined;
  try {
    const hh = await getHousehold();
    household = {
      name: hh.name ?? undefined,
      memberCount: hh.memberCount ?? hh.members?.length ?? 0,
    };
    partners = (hh.members ?? []).map((m) => ({
      userId: m.userId,
      label: m.userId === params.userId ? 'You' : m.name || 'Member',
      role: m.role,
      isViewer: m.userId === params.userId,
    }));
  } catch {
    // household optional
  }

  let homeLoan: Record<string, unknown> | null = null;
  try {
    const remote = await fetchLoanPlannerRemote(HOME_LOAN_PLANNER_ID);
    const planner = remote.planner;
    if (planner && planner.principal > 0 && !isBlankLoanPlannerSnapshot(planner)) {
      const extras = planner.extras ?? [];
      homeLoan = {
        loanId: planner.loanId,
        principal: planner.principal,
        interestRatePct: planner.interestRatePct,
        termYears: planner.termYears,
        monthlyPayment: calculateMonthlyPayment(
          planner.principal,
          planner.interestRatePct,
          planner.termYears * 12,
        ),
        extras: extras.slice(0, 40),
        extrasTotal: extras.reduce((s, e) => s + (e.amount || 0), 0),
        recurringExtra: planner.recurringExtra ?? 0,
        startMonth: planner.startMonth ?? null,
        shared: remote.shared === true,
      };
    }
  } catch {
    // home loan optional
  }

  if (!homeLoan) {
    const mortgage =
      liabilities.find((l) => l.type === 'mortgage') ??
      liabilities.find((l) => Number(l.monthlyPayment) > 0);
    if (mortgage && Number(mortgage.balance) > 0) {
      homeLoan = {
        loanId: mortgage.liabilityId,
        principal: mortgage.balance,
        interestRatePct: mortgage.interestRatePct ?? 0,
        termYears: mortgage.termYears ?? 25,
        monthlyPayment: mortgage.monthlyPayment,
        extras: [],
        extrasTotal: 0,
        recurringExtra: 0,
        startMonth: null,
        shared: mortgage.ownerUserId !== params.userId,
      };
    }
  }

  const profile = {
    name: params.name?.trim() || undefined,
    plan: params.plan,
    monthlyIncome: params.monthlyIncome,
    birthYear: params.birthYear,
    retirementAge: params.retirementAge,
    assumedReturnPct: params.assumedReturnPct,
  };

  const portfolioTotal = typeof portfolio.total === 'number' ? portfolio.total : 0;
  const projectedMonthlySurplus =
    typeof params.monthlyIncome === 'number' && params.monthlyIncome > 0
      ? Math.round((params.monthlyIncome - analysis.spendTotal - liabilitiesMonthlyPaymentTotal) * 100) /
        100
      : null;

  const uploadSuggestion =
    analysis.completeness !== 'complete'
      ? `Import a full statement for ${analysis.periodKey} (${analysis.coverageNote}).`
      : skipped[0]
        ? `Import the rest of ${skipped[0].periodKey} (${skipped[0].coverageNote}) — habit analysis skipped that month and used ${analysis.periodKey} instead.`
        : withActivity.find((m) => m.periodKey === latestKey && m.completeness !== 'complete')
          ? `Current month ${latestKey} is incomplete — import the rest for MTD accuracy. Habit answers use ${analysis.periodKey}.`
          : null;

  const guidance = [
    `Primary analysis period is ${analysis.periodKey} (${analysis.completeness}).`,
    analysis.coverageNote,
    skipped.length
      ? `Skipped incomplete newer month(s): ${skipped.map((s) => `${s.periodKey} (${s.coverageNote})`).join('; ')}.`
      : '',
    'Use only these numbers. Do not invent transactions. If asked about a skipped/partial month, say what is missing and suggest upload.',
    'Portfolio figures are from the latest uploaded snapshot.',
    homeLoan
      ? `Home loan planner: principal ${homeLoan.principal}, rate ${homeLoan.interestRatePct}%, term ${homeLoan.termYears}y.`
      : 'No home-loan planner is saved — do not invent a mortgage.',
    params.birthYear
      ? `Profile birth year ${params.birthYear}${params.retirementAge ? `, retire at ${params.retirementAge}` : ''}.`
      : 'Birth year not set — do not invent the user\'s age.',
  ]
    .filter(Boolean)
    .join(' ');

  return {
    source: 'client_finance_context',
    view,
    user: params.plan ? { plan: params.plan } : undefined,
    profile,
    homeLoan,
    household,
    periodKey: analysis.periodKey,
    monthlyIncome: params.monthlyIncome,
    spendByCategory: analysis.categories,
    topMerchants: [],
    months: monthResults,
    analysisMonth: analysis,
    portfolio,
    portfolioTotal,
    analysisMonthSpendTotal: analysis.spendTotal,
    liabilities,
    liabilitiesTotal,
    liabilitiesMonthlyPaymentTotal,
    partners,
    projectedMonthlySurplus,
    dataQuality: {
      analysisPeriodKey: analysis.periodKey,
      analysisCompleteness: analysis.completeness,
      analysisIsCurrentMonth: analysis.periodKey === latestKey,
      guidance,
      uploadSuggestion,
      availableMonths: withActivity.map((m) => ({
        periodKey: m.periodKey,
        completeness: m.completeness,
        txCount: m.txCount,
        spendTotal: m.spendTotal,
        coverageNote: m.coverageNote,
      })),
      skippedIncompleteMonths: skipped.map((s) => s.periodKey),
    },
  };
}
